// src/pages/EmailsPage/EmailsPage.jsx
import React, { useEffect, useState, useRef } from "react";
import "./EmailsPage.css";
import { Search, X } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_URL || "";

function isoToLocalShort(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

export default function EmailsPage() {
  const [emails, setEmails] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [limit] = useState(8);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const detailRef = useRef(null);

  const token = localStorage.getItem("token");

  async function apiGet(path) {
    const headers = {
      "Content-Type": "application/json",
    };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const res = await fetch(`${API_BASE}${path}`, {
      headers,
      credentials: "include",
      cache: "no-store",
    });

    if (res.status === 403) {
      setError("Invalid or expired token. Please log in again.");
      console.warn("⚠️ Invalid token – please log in");
      throw new Error("Invalid token");
    }

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || `HTTP ${res.status}`);
    }

    // attempt to parse JSON; fallback to text
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) return res.json();
    return res.text().then((t) => {
      try {
        return JSON.parse(t);
      } catch {
        return t;
      }
    });
  }

  async function load() {
    setLoading(true);
    setError("");
    try {
      const q = encodeURIComponent(query.trim());
      const res = await apiGet(`/api/emails?limit=${limit}&offset=${offset}${q ? `&q=${q}` : ""}`);

      // Accept multiple shapes: { rows, total } or array
      if (Array.isArray(res)) {
        setEmails(res);
        setTotal(res.length);
      } else if (res && Array.isArray(res.rows)) {
        setEmails(res.rows);
        setTotal(Number(res.total ?? res.rows.length ?? 0));
      } else if (res && res.rows === undefined && res.items && Array.isArray(res.items)) {
        setEmails(res.items);
        setTotal(Number(res.total ?? res.items.length ?? 0));
      } else {
        // fallback: try to coerce
        const arr = Array.isArray(res) ? res : res?.rows ?? [];
        setEmails(Array.isArray(arr) ? arr : []);
        setTotal(Number(res?.total ?? (Array.isArray(arr) ? arr.length : 0)));
      }
    } catch (err) {
      console.error("❌ Failed to load emails:", err);
      setEmails([]);
      setTotal(0);
      setError(err.message || "Failed to load emails");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  // debounce search
  useEffect(() => {
    const id = setTimeout(() => {
      setOffset(0);
      load();
    }, 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  async function openDetail(id) {
    setDetailLoading(true);
    setError("");
    try {
      const data = await apiGet(`/api/emails/${id}`);
      setSelected(data);
    } catch (err) {
      console.error("Failed to fetch email detail:", err);
      setError(err.message || "Failed to load email");
    } finally {
      setDetailLoading(false);
    }
  }

  function closeDetail() {
    setSelected(null);
  }

  function handleOverlay(e) {
    if (detailRef.current && !detailRef.current.contains(e.target)) closeDetail();
  }

  const page = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil((Number(total) || 0) / limit));

  return (
    <div className="nm-emails-page">
      <div className="nm-emails-header">
        <div>
          <h2 id="nm-emails-title">Emails</h2>
          <div style={{ color: "var(--nm-muted)", marginTop: 6 }}>
            Showing {emails.length} of {total} message{total === 1 ? "" : "s"}
          </div>
        </div>

        <div className="nm-emails-actions" role="region" aria-label="Email actions">
          <div className="nm-emails-search" aria-hidden={false}>
            <Search size={16} className="nm-search-icon" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by subject, from, to..."
              className="nm-search-input"
              aria-label="Search emails"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                title="Clear search"
                aria-label="Clear search"
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: 6,
                  marginLeft: 8,
                }}
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="nm-emails-body">
        {error && <div className="nm-error" role="alert">{error}</div>}

        <div className="nm-emails-list-wrapper">
          {loading ? (
            <div className="nm-loading">Loading emails…</div>
          ) : emails.length === 0 ? (
            <div className="nm-empty">No emails found</div>
          ) : (
            <table className="nm-emails-table" role="table" aria-labelledby="nm-emails-title">
              <thead>
                <tr>
                  <th>From</th>
                  <th>Subject</th>
                  <th>Snippet</th>
                  <th>Direction</th>
                  <th>Received</th>
                </tr>
              </thead>
              <tbody>
                {emails.map((m) => (
                  <tr
                    key={m.id}
                    className="nm-email-row"
                    onClick={() => openDetail(m.id)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") openDetail(m.id);
                    }}
                    aria-label={`Open email ${m.subject || m.from_email}`}
                  >
                    <td>{m.from_email || "-"}</td>
                    <td>{m.subject || "(no subject)"}</td>
                    <td className="nm-col-snippet">
                      {m.snippet ||
                        (m.body ? String(m.body).slice(0, 120) + (m.body.length > 120 ? "…" : "") : "-")}
                    </td>
                    <td>{m.direction || "-"}</td>
                    <td>{isoToLocalShort(m.received_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="nm-pagination" aria-label="Email pagination">
            <button
              disabled={page <= 1 || loading}
              onClick={() => setOffset(Math.max(0, offset - limit))}
              aria-label="Previous page"
            >
              Prev
            </button>

            <span>Page {page} / {totalPages}</span>

            <button
              disabled={page >= totalPages || loading}
              onClick={() => setOffset(Math.min(offset + limit, (totalPages - 1) * limit))}
              aria-label="Next page"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {selected && (
        <div className="nm-email-modal-overlay" onMouseDown={handleOverlay} role="dialog" aria-modal="true">
          <div className="nm-email-modal" ref={detailRef} onMouseDown={(e) => e.stopPropagation()}>
            <button className="nm-modal-close" aria-label="Close" onClick={closeDetail}><X size={18} /></button>

            {detailLoading ? (
              <div className="nm-loading">Loading…</div>
            ) : (
              <div className="nm-email-detail">
                <h3>{selected.subject || "(no subject)"}</h3>

                <div className="nm-email-meta" role="group" aria-label="Email meta">
                  <div><strong>From:</strong> {selected.from_email || "-"}</div>
                  <div><strong>To:</strong> {selected.to_email || "-"}</div>
                  <div><strong>Direction:</strong> {selected.direction || "-"}</div>
                  <div><strong>Received:</strong> {isoToLocalShort(selected.received_at)}</div>
                  {selected.message_id && (
                    <div><strong>Message ID:</strong> <code>{selected.message_id}</code></div>
                  )}
                </div>

                <div className="nm-divider" />

                <div className="nm-email-body" role="document">
                  <pre>{selected.body || selected.snippet || "(no content)"}</pre>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
